import type { UIIconName } from '@bravemove/design-tokens/icons';
import type { Permission } from '@bravemove/types';

/**
 * F06 — admin side-nav schema. The icon name must exist in `UI_REGISTRY`
 * (verified at type-check time). The optional `permission` is matched against
 * `adminUser.permissions` — undefined means everyone with a session sees it.
 */
export interface NavItem {
  readonly id: string;
  readonly labelKey: string;
  readonly iconName: UIIconName;
  readonly route: string;
  readonly permission?: Permission;
}

/**
 * Two visual groups in the side nav:
 *   - operations: day-to-day work (dashboard, modules, account)
 *   - platform:   identity + audit surfaces (super-admin tooling)
 *
 * Routes without an explicit `permission` are visible to every authenticated
 * actor; routes with one are filtered by `adminUser.permissions.includes(...)`.
 */
export const OPERATIONS_NAV: readonly NavItem[] = [
  { id: 'dashboard', labelKey: 'admin:nav.dashboard', iconName: 'home', route: '/' },
  { id: 'venues', labelKey: 'admin:nav.venues', iconName: 'pin', route: '/venues' },
  // F43 — the consumer-user management surface replaced the F06 placeholder;
  // gate the item on `users:read` (every admin role holds it).
  {
    id: 'users',
    labelKey: 'admin:nav.users',
    iconName: 'team',
    route: '/users',
    permission: 'users:read',
  },
  // F09 v2 — Verifications module (requests / candidates / watchlist). Read is
  // `verifications:read` (super_admin + support + content_moderator); the
  // decide actions inside are gated on `verifications:review`. Pending-count
  // badge polled every 60s by `NavCountsProvider`.
  {
    id: 'verifications',
    labelKey: 'admin:verification.nav.verifications',
    iconName: 'shield',
    route: '/verifications',
    permission: 'verifications:read',
  },
  {
    id: 'venue-claims',
    labelKey: 'admin:claim.nav.venue_claims',
    iconName: 'receipt',
    route: '/venue-claims',
    // F42 D5 split: the queue/detail/docs READ surface is `venue_claims:read`
    // (support + finance hold it); the decide actions inside the review page
    // are additionally gated on `venue_claims:review`.
    permission: 'venue_claims:read',
  },
  {
    id: 'b2b-accounts',
    labelKey: 'admin:claim.nav.b2b_accounts',
    iconName: 'team',
    route: '/b2b-accounts',
    permission: 'b2b_accounts:read',
  },
  {
    id: 'venue-photos',
    labelKey: 'admin:venue-editor.nav.venuePhotos',
    iconName: 'eye',
    route: '/venue-photos',
    permission: 'venue_photos:moderate',
  },
  // F16/F17 — read-only admin bookings list. Reachable globally here and as the
  // F42 venue-detail "Bookings" read-through (`/bookings?venueId=`). Gated on
  // `bookings:read` (super_admin + support + finance — content_moderator excluded).
  {
    id: 'bookings',
    labelKey: 'admin:nav.bookings',
    iconName: 'calendar',
    route: '/bookings',
    permission: 'bookings:read',
  },
  {
    id: 'financial',
    labelKey: 'admin:nav.financial',
    iconName: 'wallet',
    route: '/financial',
    permission: 'currency:read',
  },
  // F44 — Admin Financial Dashboard. The whole module (dashboard + settlements
  // + refunds + exports + alert-rules) is gated on `financial:read` (finance +
  // super_admin). Shows a needs-reconciliation pending-count badge polled every
  // 60s by `NavCountsProvider`. Base route `/finance` (NOT `/financial`,
  // which is F06's currency-rate editor above).
  {
    id: 'finance',
    labelKey: 'admin:nav.finance',
    iconName: 'wallet',
    route: '/finance',
    permission: 'financial:read',
  },
  // F45 — Admin Analytics & Reporting. Read-only, gated on `analytics:read`
  // (all 4 admin roles — D4). Builds the /analytics dashboard + sub-routes.
  {
    id: 'analytics',
    labelKey: 'admin:nav.analytics',
    // `pulse` (BraveMoveGlyphIcon) is the line-chart/waveform glyph — the closest
    // analytics metaphor in the UI registry (no dedicated chart icon exists).
    iconName: 'pulse',
    route: '/analytics',
    permission: 'analytics:read',
  },
  // F17 (Wave 2C) — read-only platform-default cancellation policy editor.
  {
    id: 'cancellation-policy',
    labelKey: 'admin:nav.cancellationPolicy',
    iconName: 'receipt',
    route: '/cancellation-policy',
    permission: 'bookings:edit_cancellation_policy',
  },
  // F20 (Wave 2D) — admin dispute queue + cross-venue payments activity feed.
  // Dispute queue is gated on `payment_disputes:read` (super_admin + support +
  // finance). The Resolve action inside the detail page is additionally gated
  // on `payment_disputes:resolve` (super_admin + support only — finance is
  // read-only on disputes per Decision 7).
  {
    id: 'payment-disputes',
    labelKey: 'admin:nav.paymentDisputes',
    iconName: 'wallet',
    route: '/payment-disputes',
    permission: 'payment_disputes:read',
  },
  // The payments activity feed is informational only (Decision 1 / Decision 17)
  // — no worklist actions. Gated on `payments:read` (super_admin + support +
  // finance; not content_moderator).
  {
    id: 'payments',
    labelKey: 'admin:nav.payments',
    iconName: 'receipt',
    route: '/payments',
    permission: 'payments:read',
  },
  // F23 — Commission rates + statements queue. The "Statements" item is the
  // actionable side (Mark Settled / Void) and shows a pending-count badge
  // (issued + not yet settled) polled every 60 s by
  // `NavCountsProvider`. The "Rates" item is hidden for
  // actors without `commission_rate:read` (e.g. content_moderator) per the
  // F23 Decision 10 RBAC matrix.
  {
    id: 'commission-rates',
    labelKey: 'admin:commission.sidebar.rates',
    iconName: 'wallet',
    route: '/commission-rates',
    permission: 'commission_rate:read',
  },
  {
    id: 'commission-statements',
    labelKey: 'admin:commission.sidebar.statements',
    iconName: 'receipt',
    route: '/commission-statements',
    permission: 'payments:read',
  },
  // F38 — B2B SaaS subscription tiers. The "Subscription tiers" item is the
  // tier-catalog pricing editor (read-gated on `subscription_tiers:read`; the
  // Edit pricing CTA inside is additionally gated on `subscription_tiers:write`).
  // The "Venue subscriptions" item is the per-venue list (read-gated on
  // `payments:read`); the detail page's Override / Grant trial / Manual invoice
  // actions are additionally gated on `venue_subscription:override`. Subscription
  // invoices appear in the existing commission-statements queue (its `source`
  // filter gains venue_subscription + venue_hybrid) — no second queue.
  {
    id: 'subscription-tiers',
    labelKey: 'admin:subscription.nav.tiers',
    iconName: 'wallet',
    route: '/subscription-tiers',
    permission: 'subscription_tiers:read',
  },
  {
    id: 'venue-subscriptions',
    labelKey: 'admin:subscription.nav.venueSubscriptions',
    iconName: 'receipt',
    route: '/venue-subscriptions',
    permission: 'payments:read',
  },
  // F39 — Featured placements (paid ads). Four items in the Revenue group:
  //   - "Placement inventory" (CMS — read-gated on `placement_inventory:read`;
  //     the Edit CTA inside is additionally gated on `placement_inventory:write`).
  //   - "Placement approvals" (the actionable FIFO pre-approval queue; Approve /
  //     Reject inside gated on `placement_approvals:approve`) — shows a
  //     pending-count badge (pending_approval) polled every 60s by
  //     `NavCountsProvider`. Read-gated on
  //     `placement_approvals:review`.
  //   - "Placements" (the cross-venue active monitor — read-gated on
  //     `placement_inventory:read`; override-cancel gated inside on
  //     `placement_approvals:approve`; credit-grant gated inside on
  //     `placement_credits:grant`).
  //   - "Placement analytics" (platform performance — read-gated on
  //     `placement_inventory:read`).
  {
    id: 'placement-inventory',
    labelKey: 'admin:placement.nav.inventory',
    iconName: 'wallet',
    route: '/placement-inventory',
    permission: 'placement_inventory:read',
  },
  {
    id: 'placement-approvals',
    labelKey: 'admin:placement.nav.approvals',
    iconName: 'shield',
    route: '/placement-approvals',
    permission: 'placement_approvals:review',
  },
  {
    id: 'placements',
    labelKey: 'admin:placement.nav.placements',
    iconName: 'receipt',
    route: '/placements',
    permission: 'placement_inventory:read',
  },
  {
    id: 'placement-analytics',
    labelKey: 'admin:placement.nav.analytics',
    iconName: 'cup',
    route: '/placements/analytics',
    permission: 'placement_inventory:read',
  },
  // F40 — Promotions moderation (Revenue group). Two items:
  //   - "Promotions" (the cross-venue moderation list — read-gated on
  //     `promotions:read`; the detail page's Suspend/Restore actions are
  //     additionally gated inside on `promotions:moderate`). Shows a
  //     suspended-count badge polled every 60s by
  //     `NavCountsProvider`.
  //   - "Promotion analytics" (platform performance — read-gated on
  //     `promotions:read`).
  {
    id: 'promotions',
    labelKey: 'admin:promotion.nav.promotions',
    iconName: 'receipt',
    route: '/promotions',
    permission: 'promotions:read',
  },
  {
    id: 'promotion-analytics',
    labelKey: 'admin:promotion.nav.promotionAnalytics',
    iconName: 'cup',
    route: '/promotions/analytics',
    permission: 'promotions:read',
  },
  // F41 — Tournament & event listings moderation (Revenue group). Three items:
  //   - "Tournaments" (the cross-org moderation list — read-gated on
  //     `tournaments:read`; the detail page's Suspend / Unsuspend / Edit /
  //     Cancel / Force-refund actions are additionally gated inside on
  //     `tournaments:moderate`). Shows a suspended-count badge polled every 60s
  //     by `NavCountsProvider` (post-hoc moderation — no
  //     pre-approval queue, D13).
  //   - "Organizers" (the organizer-approval surface — approve/suspend a
  //     federation/league organizer partner gated on
  //     `tournaments:approve_organizer`). Read-gated on the same permission.
  //   - "Tournament analytics" (platform performance — read-gated on
  //     `tournaments:read`). The static `/tournaments/analytics` route MUST
  //     precede the dynamic `/tournaments/:id` route in App.tsx.
  {
    id: 'tournaments',
    labelKey: 'admin:tournament.nav.tournaments',
    iconName: 'cup',
    route: '/tournaments',
    permission: 'tournaments:read',
  },
  {
    id: 'tournament-organizers',
    labelKey: 'admin:tournament.nav.organizers',
    iconName: 'shield',
    route: '/organizers',
    permission: 'tournaments:approve_organizer',
  },
  {
    id: 'tournament-analytics',
    labelKey: 'admin:tournament.nav.tournamentAnalytics',
    iconName: 'cup',
    route: '/tournaments/analytics',
    permission: 'tournaments:read',
  },
  // F29 — Teams moderation surface. No pending-count badge: per D11, F29 has
  // NO admin queue. Moderation is reactive (a member reports → admin
  // investigates). The page is a search + browse surface, not a worklist.
  // Read-gated on `teams:read` (super_admin + support + content_moderator);
  // the in-page action bar is additionally gated on `teams:moderate`
  // (super_admin + content_moderator).
  {
    id: 'teams',
    labelKey: 'admin:team.admin.nav.teams',
    iconName: 'team',
    route: '/teams',
    permission: 'teams:read',
  },
  // F31 — Skill-based matching. The "Skill disputes" item is the actionable
  // queue (Resolve gated on `skill_disputes:resolve`) + shows a pending-count
  // badge (open disputes) polled every 60s by `NavCountsProvider`.
  // Read-gated on `skill_disputes:resolve` (super_admin + support). The "Level
  // descriptors" CMS is read-gated on `sport_skill_descriptors:read` (every
  // admin role); the edit/delete actions are additionally gated on
  // `sport_skill_descriptors:write` (super_admin + content_moderator).
  {
    id: 'skill-disputes',
    labelKey: 'admin:skill.nav.skillDisputes',
    iconName: 'shield',
    route: '/skill-disputes',
    permission: 'skill_disputes:resolve',
  },
  {
    id: 'sport-skill-descriptors',
    labelKey: 'admin:skill.nav.sportSkillDescriptors',
    iconName: 'edit',
    route: '/sport-skill-descriptors',
    permission: 'sport_skill_descriptors:read',
  },
  // F32 — Open games marketplace moderation. The "Open games" item is the
  // search/browse surface (suspend/unsuspend gated inside on
  // `open_games:moderate`); the "Open game reports" item is the actionable
  // queue (Resolve gated on `open_games:moderate`) and shows a pending-count
  // badge (open reports) polled every 60s by `NavCountsProvider`.
  // Both read-gated on `open_games:read` (super_admin + support +
  // content_moderator). Admin is suspend/view-only (D20).
  {
    id: 'open-games',
    labelKey: 'admin:open-game.nav.openGames',
    iconName: 'team',
    route: '/open-games',
    permission: 'open_games:read',
  },
  {
    id: 'open-game-reports',
    labelKey: 'admin:open-game.nav.openGameReports',
    iconName: 'flag',
    route: '/open-game-reports',
    permission: 'open_games:read',
  },
  // F33 — "Trainer certifications" (renamed by the F09 v2 merge). The item is
  // the actionable FIFO pending-cert queue (trainers with a `pending` cert) and
  // shows a pending-count badge polled every 60s by
  // `NavCountsProvider`. The detail page's per-cert
  // verify/reject/revoke + Suspend/Reinstate/Force-deactivate actions are all
  // gated on the single `trainer_verifications:review` permission (super_admin
  // + content_moderator). The trainer tick itself is decided under /verifications.
  {
    id: 'trainer-verifications',
    labelKey: 'admin:trainer-verification.nav.trainerVerifications',
    iconName: 'shield',
    route: '/trainer-verifications',
    permission: 'trainer_verifications:review',
  },
  // F34 — Trainer package-consumption reversal worklist. Lookup-by-id + reverse
  // a completed/no-show decrement (credits the session back). No pending-count
  // badge: there is no queue at F34 (no GET-consumption read endpoint) — this is
  // a reactive incident-response tool. Read + write gated on the single
  // `trainer_package_consumption:reverse` permission (super_admin + support).
  {
    id: 'trainer-package-consumptions',
    labelKey: 'admin:trainer-dashboard.reverse.nav',
    iconName: 'receipt',
    route: '/trainer-package-consumptions',
    permission: 'trainer_package_consumption:reverse',
  },
  // F36 — Club moderation surface (trainer-managed teams / club archetype). The
  // "Clubs" item is a search + browse surface; the detail page's Suspend /
  // Reinstate actions are gated on `clubs:moderate`. F09 v2 retired the club
  // "Verify" action + its awaiting-verification badge: the club tick is an F09
  // request, linked from the detail page. Read-gated on `clubs:read`
  // (super_admin + support + content_moderator).
  {
    id: 'clubs',
    labelKey: 'admin:club.nav.clubs',
    iconName: 'team',
    route: '/clubs',
    permission: 'clubs:read',
  },
  // F37 — Player↔trainer recommendations. The moderation surface
  // ("Reviews & endorsements") is the actionable queue (hide/remove/restore/
  // edit/lock gated on `reviews:moderate`; endorsement remove/restore gated on
  // `endorsements:moderate`) and shows a pending-flagged-count badge polled
  // every 60s by `NavCountsProvider`. The two catalog CMS items
  // (badge + review-tag) are read-gated on `recommendation_catalog:read`
  // (super_admin + content_moderator + support); the Add/Edit/Delete actions
  // are additionally gated inside on `recommendation_catalog:write`.
  {
    id: 'review-moderation',
    labelKey: 'admin:recommendation.nav.moderation',
    iconName: 'shield',
    route: '/review-moderation',
    permission: 'reviews:moderate',
  },
  {
    id: 'badge-catalog',
    labelKey: 'admin:recommendation.nav.badges',
    iconName: 'star',
    route: '/recommendation-catalog/badges',
    permission: 'recommendation_catalog:read',
  },
  {
    id: 'review-tags',
    labelKey: 'admin:recommendation.nav.tags',
    iconName: 'bookmark',
    route: '/recommendation-catalog/tags',
    permission: 'recommendation_catalog:read',
  },
  // F43 — central report queue (read-gated on `reports:read`; the detail page's
  // claim/action/dismiss/reassign + chat soft-delete are gated inside on
  // `reports:resolve` / `chat:moderate`). Shows an open-report-count badge polled
  // every 60s by `NavCountsProvider`.
  {
    id: 'reports',
    labelKey: 'admin:f43.nav.reports',
    iconName: 'flag',
    route: '/reports',
    permission: 'reports:read',
  },
  // F43 — GDPR request-tracking processor (read-gated on `gdpr_requests:read`;
  // the process/reject flow is gated inside on `gdpr_requests:process`). Shows a
  // pending (open + in_review) count badge polled every 60s by
  // `NavCountsProvider`.
  {
    id: 'gdpr-requests',
    labelKey: 'admin:f43.nav.gdpr',
    iconName: 'shield',
    route: '/gdpr',
    permission: 'gdpr_requests:read',
  },
];

export const PLATFORM_NAV: readonly NavItem[] = [
  {
    id: 'admin-users',
    labelKey: 'admin:nav.adminUsers',
    iconName: 'shield',
    route: '/admin-users',
    permission: 'admin_users:read',
  },
  {
    id: 'audit-log',
    labelKey: 'admin:nav.auditLog',
    iconName: 'receipt',
    route: '/audit-log',
    permission: 'audit:read',
  },
  // F60 — production API logs + health (super_admin only).
  {
    id: 'ops-logs',
    labelKey: 'admin:nav.opsLogs',
    iconName: 'search',
    route: '/ops/logs',
    permission: 'ops_logs:read',
  },
  { id: 'account', labelKey: 'admin:nav.account', iconName: 'settings', route: '/account' },
];

/**
 * F26 (Wave 2D) — "Tools" section, separate from operational and platform-
 * identity surfaces. dry-run + test-send are gated on `notifications:dry_run`
 * (super_admin only — Decision 14).
 *
 * The admin in-app inbox + preferences entries were removed: the F26 inbox is
 * consumer/B2B-only (`in_app_notifications.recipient_user_id` FKs `users.id`,
 * not admins), so those pages 401'd / had no rows to show. Admins are alerted
 * by email + the sidebar pending-count badges. A proper admin-recipient inbox
 * is a post-MVP F26 follow-up.
 */
export const TOOLS_NAV: readonly NavItem[] = [
  {
    id: 'notifications-dry-run',
    labelKey: 'admin:nav.notificationsDryRun',
    iconName: 'eye',
    route: '/admin/notifications/dry-run',
    permission: 'notifications:dry_run',
  },
  {
    id: 'notifications-test-send',
    labelKey: 'admin:nav.notificationsTestSend',
    iconName: 'share',
    route: '/admin/notifications/test-send',
    permission: 'notifications:dry_run',
  },
  // F28 (Wave 2C-ii) — email-suppressions admin queue. Read-gated on
  // `email_suppressions:read` (super_admin + support + content_moderator);
  // the page-level RBAC hides the Add CTA for content_moderator. The UI
  // icon registry has no `mail` glyph at F28; `flag` is the closest
  // semantic match for a suppression / blocked-list surface.
  {
    id: 'email-suppressions',
    labelKey: 'admin:nav.emailSuppressions',
    iconName: 'flag',
    route: '/email-suppressions',
    permission: 'email_suppressions:read',
  },
  // F27 (Wave 2E) — WhatsApp admin tooling: templates registry + quality
  // state. Both read-gated on `whatsapp:read` (super_admin + support +
  // finance + content_moderator); the Sync action on the templates page is
  // additionally gated on `whatsapp:write` (super_admin only). The
  // templates item shows a pending-count badge (PENDING + REJECTED +
  // PAUSED + IN_APPEAL) polled every 60s.
  {
    id: 'whatsapp-templates',
    labelKey: 'admin:nav.whatsappTemplates',
    iconName: 'bell',
    route: '/whatsapp/templates',
    permission: 'whatsapp:read',
  },
  {
    id: 'whatsapp-quality',
    labelKey: 'admin:nav.whatsappQuality',
    iconName: 'shield',
    route: '/whatsapp/quality',
    permission: 'whatsapp:read',
  },
];

/** Filter a group against the actor's permission set. */
export function filterByPermissions(
  items: readonly NavItem[],
  permissions: readonly Permission[],
): readonly NavItem[] {
  return items.filter((item) => !item.permission || permissions.includes(item.permission));
}
